import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useInvalidateHomeState } from '../../home/hooks/use-home-state'
import { outreachApi, outreachStatusQueryKey } from '../api'

// Faster polling during the activation flow so the founder doesn't sit on a
// stale "waiting…" UI for up to 30s after they accept the HeyReach invitation
// or connect their LinkedIn. Steady state goes back to 30s once the campaign
// is past the AWAITING_* states.
const TRANSITIONAL_STATUSES = new Set([
  'PENDING_SEAT',
  'PENDING_PROVISIONING',
  'AWAITING_SENDER',
  'AWAITING_CAMPAIGN',
])
const FAST_POLL_MS = 4_000
const STEADY_POLL_MS = 30_000

export function useOutreachStatusQuery(startupId: string) {
  const query = useQuery({
    queryKey: outreachStatusQueryKey(startupId),
    queryFn: () => outreachApi.getStatus(startupId),
    refetchInterval: (q) => {
      const status = q.state.data?.status
      if (!status) return FAST_POLL_MS
      return TRANSITIONAL_STATUSES.has(status) ? FAST_POLL_MS : STEADY_POLL_MS
    },
  })

  // The activation statuses change on their own — a seat is granted, a
  // workspace finishes provisioning, a sender attaches — and each one moves
  // Home to a different card. This query notices within four seconds; Home
  // polls on its own much slower clock, so without this the founder watched the
  // setup page advance while Home sat on the previous step. Pushing the change
  // across costs nothing: it only fires on an actual transition.
  const invalidateHome = useInvalidateHomeState(startupId)
  const previousStatus = useRef<string | null | undefined>(undefined)
  const status = query.data?.status
  useEffect(() => {
    const had = previousStatus.current
    previousStatus.current = status
    if (had !== undefined && had !== status) void invalidateHome()
  }, [status, invalidateHome])

  return query
}
